import React, { useEffect, useState } from 'react';
import { XIcon, HeartIcon, ZapIcon } from 'lucide-react';
import { useSound } from './SoundEffects';
export const DonationBanner = ({
  onCursorChange
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(68); // 68% funded
  const {
    playSound
  } = useSound();
  const handleClose = () => {
    playSound('click');
    setIsVisible(false);
  };
  const handleDonate = () => {
    playSound('success');
    window.open('https://ko-fi.com/cherylqian', '_blank');
  };
  // Subtle animation for the progress bar
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        // Randomly fluctuate between -0.5% and +0.5% for visual effect
        const fluctuation = (Math.random() - 0.5) * 0.5;
        return Math.min(100, Math.max(0, prev + fluctuation));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  if (!isVisible) return null;
  return <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pointer-events-none" data-id="element-560">
      <div className="mx-auto max-w-4xl glass border border-[#6ff8f0]/30 rounded-lg overflow-hidden shadow-lg pointer-events-auto animate-fade-in" style={{
      boxShadow: '0 0 20px rgba(111, 248, 240, 0.2)'
    }} data-id="element-561">
        <div className="relative" data-id="element-562">
          {/* Background circuit pattern */}
          <div className="absolute inset-0 opacity-10" data-id="element-563">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-564">
              <pattern id="circuit" width="50" height="50" patternUnits="userSpaceOnUse" data-id="element-565">
                <path d="M10 10 H40 V40 H10 Z" fill="none" stroke="#6ff8f0" strokeWidth="0.5" data-id="element-566" />
                <circle cx="10" cy="10" r="2" fill="#6ff8f0" data-id="element-567" />
                <circle cx="40" cy="40" r="2" fill="#e64cff" data-id="element-568" />
              </pattern>
              <rect width="100%" height="100%" fill="url(#circuit)" data-id="element-569" />
            </svg>
          </div>
          {/* Close button */}
          <button onClick={handleClose} className="absolute top-3 right-3 text-[#a0c0e0] hover:text-[#6ff8f0] transition-colors duration-300 z-10" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label="Close donation banner" data-id="element-570">
            <XIcon size={18} data-id="element-571" />
          </button>
          <div className="p-4 md:p-6 relative z-0" data-id="element-572">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8" data-id="element-573">
              {/* Icon/Logo */}
              <div className="flex-shrink-0 w-16 h-16 rounded-full glass border border-[#e64cff]/50 flex items-center justify-center relative group" data-id="element-574">
                <HeartIcon size={28} className="text-[#e64cff] group-hover:scale-110 transition-transform duration-300" data-id="element-575" />
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#e64cff]/20 to-[#6ff8f0]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-576"></div>
                {/* Pulse effect */}
                <div className="absolute inset-0 rounded-full border border-[#e64cff]/50 animate-ping opacity-30" data-id="element-577"></div>
              </div>
              {/* Content */}
              <div className="flex-1 text-center md:text-left" data-id="element-578">
                <h3 className="text-lg md:text-xl font-bold font-heading text-gradient-purple mb-1" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-579">
                  PROJECT ORCHID FUNDRAISER
                </h3>
                <p className="text-[#a0c0e0] text-sm md:text-base font-mono mb-3" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-580">
                  Support the next generation of biological-digital symbiosis
                  experiments
                </p>
                {/* Progress bar */}
                <div className="w-full bg-[#1a2a40] h-2 rounded-full overflow-hidden mb-2" data-id="element-581">
                  <div className="h-full bg-gradient-to-r from-[#6ff8f0] to-[#e64cff] transition-all duration-1000 ease-in-out" style={{
                  width: `${progress}%`
                }} data-id="element-582"></div>
                </div>
                <div className="flex justify-between items-center mb-4" data-id="element-583">
                  <span className="text-xs font-mono text-[#a0c0e0]" data-id="element-584">
                    ${Math.floor(68000 * progress / 100).toLocaleString()}{' '}
                    raised
                  </span>
                  <span className="text-xs font-mono text-[#a0c0e0]" data-id="element-585">
                    Goal: $68,000
                  </span>
                </div>
              </div>
              {/* CTA Button */}
              <button onClick={handleDonate} className="px-6 py-3 glass border border-[#e64cff]/50 text-[#e64cff] hover:bg-[#e64cff]/10 transition-all duration-300 uppercase tracking-wider font-mono relative overflow-hidden group" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-586">
                <span className="relative z-10 flex items-center gap-2" data-id="element-587">
                  <ZapIcon size={16} className="animate-pulse" data-id="element-588" />
                  DONATE NOW
                </span>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#e64cff] transition-all duration-300 group-hover:w-full" data-id="element-589"></span>
                {/* Corner accents */}
                <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[#e64cff]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-590"></span>
                <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-[#e64cff]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-591"></span>
                <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-[#e64cff]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-592"></span>
                <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[#e64cff]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-593"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};